import { cleanSequence } from "@/lib/science/sequence";

// SantaLucia 1998 unified nearest-neighbor parameters, keyed by the 5'→3'
// top-strand dinucleotide. ΔH in kcal/mol, ΔS in cal/(mol·K).
const NN: Record<string, { h: number; s: number }> = {
  AA: { h: -7.9, s: -22.2 },
  AC: { h: -8.4, s: -22.4 },
  AG: { h: -7.8, s: -21.0 },
  AT: { h: -7.2, s: -20.4 },
  CA: { h: -8.5, s: -22.7 },
  CC: { h: -8.0, s: -19.9 },
  CG: { h: -10.6, s: -27.2 },
  CT: { h: -7.8, s: -21.0 },
  GA: { h: -8.2, s: -22.2 },
  GC: { h: -9.8, s: -24.4 },
  GG: { h: -8.0, s: -19.9 },
  GT: { h: -8.4, s: -22.4 },
  TA: { h: -7.2, s: -21.3 },
  TC: { h: -8.2, s: -22.2 },
  TG: { h: -8.5, s: -22.7 },
  TT: { h: -7.9, s: -22.2 },
};
// Initiation terms (SantaLucia 1998).
const INIT_GC = { h: 0.1, s: -2.8 };
const INIT_AT = { h: 2.3, s: 4.1 };
const R = 1.987; // cal/(mol·K)

/** Normalise to an unambiguous DNA sequence (U→T); returns "" if any base is not A/C/G/T. */
function toDna(input: string): string {
  const seq = cleanSequence(input).replace(/U/g, "T");
  return /^[ACGT]*$/.test(seq) ? seq : "";
}

export function baseCounts(seq: string): {
  a: number;
  c: number;
  g: number;
  t: number;
} {
  let a = 0;
  let c = 0;
  let g = 0;
  let t = 0;
  for (const ch of seq) {
    if (ch === "A") a++;
    else if (ch === "C") c++;
    else if (ch === "G") g++;
    else if (ch === "T") t++;
  }
  return { a, c, g, t };
}

/** Basic (Wallace) rule: Tm = 2(A+T) + 4(G+C). Best for oligos < 14 nt. */
export function wallaceTm(input: string): number | null {
  const seq = toDna(input);
  if (!seq) return null;
  const { a, c, g, t } = baseCounts(seq);
  return 2 * (a + t) + 4 * (g + c);
}

/** Simple GC% formula: Tm = 64.9 + 41 × (GC − 16.4) / length. */
export function gcTm(input: string): number | null {
  const seq = toDna(input);
  if (seq.length === 0) return null;
  const { c, g } = baseCounts(seq);
  return 64.9 + (41 * (g + c - 16.4)) / seq.length;
}

/**
 * Nearest-neighbor Tm (SantaLucia 1998) with salt correction, in °C.
 * `na` = monovalent cation concentration (mol/L); `ct` = total oligo strand
 * concentration (mol/L). Assumes a non-self-complementary duplex (x = 4).
 */
export function nearestNeighborTm(
  input: string,
  na = 0.05,
  ct = 0.5e-6,
): number | null {
  const seq = toDna(input);
  if (seq.length < 2 || !(na > 0) || !(ct > 0)) return null;

  let dH = 0;
  let dS = 0;
  for (let i = 0; i < seq.length - 1; i++) {
    const pair = NN[seq.slice(i, i + 2)];
    dH += pair.h;
    dS += pair.s;
  }
  // Initiation from the two terminal base pairs.
  for (const end of [seq[0], seq[seq.length - 1]]) {
    const init = end === "G" || end === "C" ? INIT_GC : INIT_AT;
    dH += init.h;
    dS += init.s;
  }

  // Salt correction (SantaLucia 1998): entropy adjustment per phosphate.
  const dSsalt = dS + 0.368 * (seq.length - 1) * Math.log(na);

  const tmK = (dH * 1000) / (dSsalt + R * Math.log(ct / 4));
  return tmK - 273.15;
}
